import React, { useState, useEffect } from 'react'
import { loadSampleIndex, loadSampleData, type SampleMetadata, type SampleData } from '../api/samples'
import { Film, Play, Clock, Eye, Sparkles, ChevronDown, X } from 'lucide-react'

interface SampleSelectorProps {
  onSampleLoad: (data: SampleData) => void
  isLoading?: boolean
}

export function SampleSelector({ onSampleLoad, isLoading = false }: SampleSelectorProps) {
  const [samples, setSamples] = useState<SampleMetadata[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loadingSample, setLoadingSample] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSamples()
  }, [])

  const loadSamples = async () => {
    try {
      const index = await loadSampleIndex()
      setSamples(index.samples)
    } catch (err) {
      console.error('Failed to load samples:', err)
      setError('Failed to load sample videos')
    }
  }

  const handleSelectSample = async (sample: SampleMetadata) => {
    setLoadingSample(sample.id)
    setError(null)
    
    try {
      const data = await loadSampleData(sample.id)
      onSampleLoad(data)
      setIsOpen(false)
    } catch (err) {
      console.error('Failed to load sample:', err)
      setError('Failed to load sample data')
    } finally {
      setLoadingSample(null)
    }
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      tech: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      comedy: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      education: 'bg-green-500/20 text-green-400 border-green-500/30',
      gaming: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      lifestyle: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    }
    return colors[category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  }

  if (isLoading) return null

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-indigo-500/25"
      >
        <Sparkles className="w-4 h-4" />
        <span>Load Sample</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)} 
          />
          
          {/* Panel */}
          <div className="absolute top-full left-0 mt-2 w-[500px] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-800/50 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Film className="w-5 h-5 text-indigo-400" />
                <span className="font-medium text-white">Sample Videos</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Sample List */}
            <div className="max-h-[400px] overflow-y-auto">
              {samples.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Film className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No sample videos available</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {samples.map((sample) => (
                    <button
                      key={sample.id}
                      onClick={() => handleSelectSample(sample)}
                      disabled={loadingSample === sample.id}
                      className="w-full flex items-start gap-4 p-3 hover:bg-gray-800/50 rounded-lg transition-all group text-left disabled:opacity-50"
                    >
                      {/* Thumbnail Placeholder */}
                      <div className="relative w-24 h-14 bg-gray-800 rounded-lg flex-shrink-0 overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-indigo-600/20 to-purple-600/20">
                          <Play className="w-6 h-6 text-white/50 group-hover:text-white transition-colors" />
                        </div>
                        {sample.featured && (
                          <div className="absolute top-1 right-1">
                            <Sparkles className="w-3 h-3 text-yellow-400" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate group-hover:text-indigo-400 transition-colors">
                          {loadingSample === sample.id ? 'Loading...' : sample.title}
                        </h4>
                        <p className="text-gray-500 text-sm truncate mt-0.5">
                          {sample.description}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            {formatDuration(sample.duration)}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs border ${getCategoryColor(sample.category)}`}>
                            {sample.category}
                          </span>
                          <span className="text-xs text-gray-500">
                            {sample.source}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 bg-gray-800/30 border-t border-gray-700 text-xs text-gray-500">
              Pre-analyzed samples with transcripts & viral segments
            </div>
          </div>
        </>
      )}
    </div>
  )
}
